import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Button,
    Chip,
    Container,
    DatePicker,
    Flex,
    Heading,
    Icon,
    Select,
    Skeleton,
    Tabs,
    Text,
    TextField,
} from "@telia/teddy";
import { format, max as maxDate, startOfDay, subDays } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import ImeiRegistrationForm from "./ImeiRegistrationForm";
import SignOrderModal from "./SignOrderModal";
import OrdersTable from "./OrdersTable";
import useCompletedOrdersTable from "./useCompletedOrdersTable";
import useManualAttentionOrdersTable from "./useManualAttentionOrdersTable";
import useOrdersMissingImeiTable from "./useOrdersMissingImeiTable";
import useOrdersMissingSigningTable from "./useOrdersMissingSigningTable";
import useOrdersFilterParams, { isOrderTab } from "./useOrdersFilterParams";
import { normalizeSearchInput, validateSearchInput } from "./orderSearchUtils";
import { FORM_ID, MISSING_IMEI_MAX_DAYS_BACK } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { clearAll } from "../../../rapidform/state/actions";
import { getSalesAgent, getSalesAgents } from "../../state/selectors/selectors";
import { getAgentChainName } from "../../../app/state/selectors";
import { getCommissionDeadlineStatus } from "../../utils";
import SupportChatPanel from "../../components/molecules/SupportChatPanel/SupportChatPanel";
import ContinueOrderModal from "../../components/molecules/ContinueOrderModal/ContinueOrderModal";
import DeleteDraftOrderModal from "../../components/molecules/DeleteDraftOrderModal/DeleteDraftOrderModal";
import { OrderItem } from "../../types/models";
import useDraftOrdersTable, { DraftOrderItem } from "../DraftOrders/useDraftOrdersTable";
import "./Orders.less";

const Orders: FC = () => {
    const dispatch = useDispatch();
    const salesAgent = useSelector(getSalesAgent);
    const salesAgents = useSelector(getSalesAgents);
    const agentChainName = useSelector(getAgentChainName);
    const isElkjop = agentChainName?.toLowerCase().includes("elkj") ?? false;
    const {
        activeTab,
        agentId: selectedAgentId,
        endDate,
        manualAttentionType,
        pageIndex,
        search,
        setActiveTab,
        setAgentId: setSelectedAgentId,
        setDateRange,
        setManualAttentionType,
        setPageIndex,
        setSearch,
        startDate,
    } = useOrdersFilterParams();
    const [searchInput, setSearchInput] = useState(search);
    const [searchError, setSearchError] = useState<string | null>(null);
    const sortedAgents = useMemo(
        () =>
            [...salesAgents].sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, "no-NB"),
            ),
        [salesAgents],
    );
    const agentSelectOptions = useMemo(() => ["all", ...sortedAgents.map((a) => String(a.id))], [sortedAgents]);

    const loggedInAgentSelectId = useMemo(() => {
        const id = sortedAgents.find((a) => a.email === salesAgent?.email)?.id;
        return id === undefined ? undefined : String(id);
    }, [sortedAgents, salesAgent?.email]);

    const myOrdersActive =
        selectedAgentId !== "" && loggedInAgentSelectId !== undefined && selectedAgentId === loggedInAgentSelectId;

    const isBackendSearchTab =
        activeTab === "completed" || activeTab === "missing-signing" || activeTab === "manual-attention";
    const normalizedSearch = normalizeSearchInput(search);
    // q may come from a client-filtered tab or a hand-edited URL, so only valid values reach the backend
    const backendSearch = validateSearchInput(normalizedSearch) ? "" : normalizedSearch;

    const missingImeiEarliestDate = startOfDay(subDays(new Date(), MISSING_IMEI_MAX_DAYS_BACK));
    const missingImeiStartDate =
        startDate && (!endDate || endDate >= missingImeiEarliestDate)
            ? maxDate([startDate, missingImeiEarliestDate])
            : startDate;

    const tableFilters = { agentId: selectedAgentId, onPageIndexChange: setPageIndex, pageIndex };
    const draft = useDraftOrdersTable({ ...tableFilters, enabled: activeTab === "draft", search });
    const handleRowClick = () => {
        dispatch(clearAll(FORM_ID.IMEI_REGISTRATION));
    };
    const missingImei = useOrdersMissingImeiTable({
        ...tableFilters,
        enabled: activeTab === "missing-imei",
        endDate,
        onRowClickCb: handleRowClick,
        search,
        startDate: missingImeiStartDate,
    });
    const allMissingImeiRows = missingImei.table.getPrePaginationRowModel().rows;
    const isExpiredMissingImei = (row: (typeof allMissingImeiRows)[number]) =>
        getCommissionDeadlineStatus(row.original.creationDate ?? "")?.label === "Frist utløpt";
    const missingImeiRowsCount = allMissingImeiRows.filter((row) => !isExpiredMissingImei(row)).length;
    const expiredMissingImeiCount = allMissingImeiRows.filter(isExpiredMissingImei).length;
    const missingSigning = useOrdersMissingSigningTable({
        ...tableFilters,
        enabled: activeTab === "missing-signing",
        endDate,
        search: backendSearch,
        startDate,
    });
    const completed = useCompletedOrdersTable({
        ...tableFilters,
        enabled: activeTab === "completed",
        endDate,
        onRowClickCb: handleRowClick,
        search: backendSearch,
        startDate,
    });
    const manualAttention = useManualAttentionOrdersTable({
        ...tableFilters,
        enabled: activeTab === "manual-attention",
        endDate,
        manualAttentionType,
        search: backendSearch,
        startDate,
    });
    const searchPlaceholder = isBackendSearchTab ? "Søk på tlf, navn, ordrenr, imei" : "Søk i alle kolonner...";

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        if (!isBackendSearchTab) setSearch(value);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter" || !isBackendSearchTab) return;
        const normalizedSearchInput = normalizeSearchInput(searchInput);
        setSearchInput(normalizedSearchInput);
        const validationError = validateSearchInput(normalizedSearchInput);
        setSearchError(validationError);
        if (!validationError) setSearch(normalizedSearchInput);
    };

    const [datePickerResetKey, setDatePickerResetKey] = useState(0);
    const hasDateFilter = Boolean(startDate || endDate);

    // DatePicker only reads `value` on mount, and URL updates run as a transition, so remount once dates are gone
    useEffect(() => {
        if (!hasDateFilter) setDatePickerResetKey((k) => k + 1);
    }, [hasDateFilter]);

    const handleClearDates = () => {
        setDateRange([null, null]);
    };

    const handleTabChange = (value: string) => {
        if (isOrderTab(value)) {
            setSearchInput(search);
            setSearchError(null);
            setActiveTab(value);
        }
    };

    // Draft orders are only showing last 3 hours so no date picker is needed
    const showDatePicker = activeTab !== "draft";

    const earliestSelectableDate = activeTab === "missing-imei" ? missingImeiEarliestDate : null;
    const displayedStartDate = activeTab === "missing-imei" ? missingImeiStartDate : startDate;
    const fromDateMin = earliestSelectableDate ? format(earliestSelectableDate, "yyyy-MM-dd") : "";
    const toDateMinDate =
        displayedStartDate && earliestSelectableDate
            ? maxDate([displayedStartDate, earliestSelectableDate])
            : (displayedStartDate ?? earliestSelectableDate);
    const toDateMin = toDateMinDate ? format(toDateMinDate, "yyyy-MM-dd") : "";

    return (
        <div className="orders">
            <Container>
                <Button
                    variant="text"
                    size="sm"
                    onClick={() => window.location.assign(PAGES.RETAILER.ORDER.CUSTOMER.URL)}
                    className="orders__back-button"
                >
                    <Icon name="arrow-left" size="sm" aria-hidden="true" focusable="false" />
                    Tilbake
                </Button>
                <Heading mb="100" as="h1" variant="title-300">
                    {activeTab === "draft"
                        ? "Ordreutkast"
                        : activeTab === "missing-imei"
                          ? "Etterregistrering av IMEI"
                          : activeTab === "missing-signing"
                            ? "Signering mangler"
                            : activeTab === "manual-attention"
                              ? "Manuell oppfølging"
                              : "Fullførte ordre"}
                </Heading>
                <Flex justify="space-between" align="flex-end" className="orders__filters">
                    <Flex gap="150" align="flex-end">
                        {showDatePicker && (
                            <>
                                <DatePicker
                                    key={`fra-${datePickerResetKey}-${activeTab}`}
                                    label="Fra"
                                    value={displayedStartDate ? format(displayedStartDate, "yyyy-MM-dd") : ""}
                                    min={fromDateMin}
                                    max={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                                    onSelectedDate={(date) => setDateRange([date, endDate ?? null])}
                                />
                                <DatePicker
                                    key={`til-${datePickerResetKey}`}
                                    label="Til"
                                    value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                                    min={toDateMin}
                                    onSelectedDate={(date) => setDateRange([startDate ?? null, date])}
                                />
                                {(startDate || endDate) && (
                                    <Button
                                        size="sm"
                                        iconOnly
                                        aria-label="Nullstill datoer"
                                        variant="text"
                                        onClick={handleClearDates}
                                    >
                                        <Icon name="close" size="sm" aria-hidden="true" focusable="false" />
                                    </Button>
                                )}
                            </>
                        )}
                        {activeTab === "draft" && <Text>Viser ordreutkast opprettet de siste 3 timene.</Text>}
                    </Flex>
                    <TextField>
                        <TextField.Label>Søk</TextField.Label>
                        <TextField.Input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="orders__search-input"
                        />
                        {isBackendSearchTab && searchError && (
                            <TextField.HelperText>{searchError}</TextField.HelperText>
                        )}
                    </TextField>
                </Flex>
                <Flex justify="between" align="center" className="orders__tabs">
                    <Tabs value={activeTab} onValueChange={handleTabChange}>
                        <Tabs.List>
                            <Tabs.Trigger value="completed">Fullførte</Tabs.Trigger>
                            <Tabs.Trigger value="missing-signing">Signering mangler</Tabs.Trigger>
                            <Tabs.Trigger value="missing-imei">IMEI-registrering</Tabs.Trigger>
                            <Tabs.Trigger value="manual-attention">Manuell oppfølging</Tabs.Trigger>
                            <Tabs.Trigger value="draft">Utkast</Tabs.Trigger>
                        </Tabs.List>
                    </Tabs>
                    <Flex direction="row" gap="150" align="center" pt="200" pb="200">
                        <Select
                            name="agentFilter"
                            options={agentSelectOptions}
                            value={selectedAgentId || "all"}
                            onValueChange={(value: string) => {
                                setSelectedAgentId(value === "all" ? "" : value);
                                return value;
                            }}
                        >
                            <Select.Trigger className="orders__agent-select-trigger">
                                <Select.Value placeholder="Alle agenter" />
                            </Select.Trigger>
                            <Select.Portal>
                                <Select.Content>
                                    <Select.Viewport>
                                        <Select.Item value="all">Alle agenter</Select.Item>
                                        {sortedAgents.map((agent) => (
                                            <Select.Item key={agent.id} value={String(agent.id)}>
                                                <span data-di-mask>
                                                    {agent.firstName} {agent.lastName}
                                                </span>
                                            </Select.Item>
                                        ))}
                                    </Select.Viewport>
                                </Select.Content>
                            </Select.Portal>
                        </Select>
                        <Chip
                            type="single"
                            value={myOrdersActive ? "mine" : ""}
                            onValueChange={(val) => {
                                if (val === "mine") {
                                    setSelectedAgentId(loggedInAgentSelectId ?? "");
                                } else {
                                    setSelectedAgentId("");
                                }
                            }}
                            variant="filter"
                        >
                            <Chip.Item value="mine">Mine ordre</Chip.Item>
                        </Chip>
                    </Flex>
                </Flex>
                {activeTab === "manual-attention" && (
                    <Flex
                        gap="300"
                        align="center"
                        className="orders__manual-attention-filter"
                        style={{ paddingBottom: "16px" }}
                    >
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "pointer",
                                fontSize: "1rem",
                            }}
                        >
                            <input
                                type="radio"
                                name="manualAttentionType"
                                value="MANUAL_ATTENTION_REQUIRED_BY_DEALER"
                                checked={manualAttentionType === "MANUAL_ATTENTION_REQUIRED_BY_DEALER"}
                                onChange={() => setManualAttentionType("MANUAL_ATTENTION_REQUIRED_BY_DEALER")}
                            />
                            Av forhandler
                        </label>
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "pointer",
                                fontSize: "1rem",
                            }}
                        >
                            <input
                                type="radio"
                                name="manualAttentionType"
                                value="INTERNAL_MANUAL_ATTENTION_REQUIRED"
                                checked={manualAttentionType === "INTERNAL_MANUAL_ATTENTION_REQUIRED"}
                                onChange={() => setManualAttentionType("INTERNAL_MANUAL_ATTENTION_REQUIRED")}
                            />
                            Av Telia
                        </label>
                    </Flex>
                )}
                {activeTab === "missing-imei" &&
                    (missingImei.isLoading ? (
                        <Skeleton height="24px" width="420px" mb="100" />
                    ) : (
                        <Text mb="100">
                            Totalt <strong>{missingImeiRowsCount}</strong> ordre mangler etterregistrering. (
                            <strong>{expiredMissingImeiCount}</strong> ordre har gått over fristen)
                        </Text>
                    ))}
                {activeTab === "draft" ? (
                    <OrdersTable<DraftOrderItem>
                        error={draft.error}
                        isLoading={draft.isLoading}
                        onRowClick={draft.onRowClick}
                        table={draft.table}
                        columns={draft.columns}
                    />
                ) : activeTab === "missing-imei" ? (
                    <OrdersTable<OrderItem>
                        error={missingImei.error}
                        isLoading={missingImei.isLoading}
                        isRowExpanded={(row) => missingImei.expandedOrderId === row.orderId}
                        onRowClick={missingImei.onRowClick}
                        renderExpandedRow={(row) => (
                            <ImeiRegistrationForm
                                orderId={row.orderId}
                                phoneNumber={row.telephoneNumber}
                                offeringName={row.offeringName}
                                onCancel={() => missingImei.setExpandedOrderId(null)}
                                onSuccess={() => {
                                    missingImei.setExpandedOrderId(null);
                                    missingImei.removeOrder(row.orderId);
                                }}
                            />
                        )}
                        table={missingImei.table}
                        columns={missingImei.columns}
                    />
                ) : activeTab === "missing-signing" ? (
                    <OrdersTable<OrderItem>
                        error={missingSigning.error}
                        isLoading={missingSigning.isLoading}
                        isRowExpanded={(row) => missingSigning.expandedOrderId === row.orderId}
                        onRowClick={missingSigning.onRowClick}
                        renderExpandedRow={(row) => (
                            <SignOrderModal
                                omOrderId={row.orderId}
                                customerPhone={row.telephoneNumber}
                                customerName={row.legalResponsibleName}
                                isElkjop={isElkjop}
                                onCancel={() => missingSigning.setExpandedOrderId(null)}
                                onSuccess={() => {
                                    missingSigning.setExpandedOrderId(null);
                                    missingSigning.removeOrder(row.orderId);
                                }}
                                onRefresh={() => missingSigning.refetch()}
                            />
                        )}
                        table={missingSigning.table}
                        columns={missingSigning.columns}
                    />
                ) : activeTab === "manual-attention" ? (
                    <OrdersTable<OrderItem>
                        error={manualAttention.error}
                        isLoading={manualAttention.isLoading}
                        onRowClick={() => undefined}
                        table={manualAttention.table}
                        columns={manualAttention.columns}
                    />
                ) : (
                    <OrdersTable<OrderItem>
                        error={completed.error}
                        getCellHref={completed.getCellHref}
                        isLoading={completed.isLoading}
                        onRowClick={completed.onRowClick}
                        table={completed.table}
                        columns={completed.columns}
                    />
                )}
            </Container>
            {draft.continueOrderRef && (
                <ContinueOrderModal orderRef={draft.continueOrderRef} onClose={() => draft.setContinueOrderRef(null)} />
            )}
            {draft.deleteOrderRef && (
                <DeleteDraftOrderModal
                    isDeleting={draft.deleteMutation.isPending}
                    error={draft.deleteMutation.error?.message}
                    onClose={() => {
                        draft.deleteMutation.reset();
                        draft.setDeleteOrderRef(null);
                    }}
                    onConfirm={() => draft.deleteMutation.mutate(draft.deleteOrderRef!)}
                />
            )}
            <SupportChatPanel
                open={manualAttention.chatOrderId !== null}
                onOpenChange={(open) => {
                    if (!open) manualAttention.setChatOrderId(null);
                }}
                initialMessage={
                    manualAttention.chatOrderId !== null
                        ? `Manuell oppfølging av Ordre-ID: ${manualAttention.chatOrderId}`
                        : undefined
                }
            />
        </div>
    );
};

export default Orders;
